import { Product } from "../../../models/Product";
import React, { RefObject, useEffect, useRef } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { Barcode } from "../../../components/Barcode";
import useStyles from "./styles";
import { Currency } from "../../../components/misc/Currency";
import { Close } from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import { useI18n } from "../../../hooks/useI18n";

interface Props {
  products: Product[];
  onClose: () => void;
}

export const ProductsPrint = ({ products, onClose }: Props) => {
  const classes = useStyles();
  const { t } = useI18n();

  let componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current
  });
  // Products with longer names might cause line breaks and higher labels to be printed.
  // Try to put them near each other
  let printableProducts = [...products].sort(
    (a, b) => a.name.length - b.name.length
  );

  const [unit, setUnit] = React.useState(
    localStorage.getItem("print-unit") || "cm"
  );
  const [width, setWidth] = React.useState(
    localStorage.getItem("print-width") || "7"
  );
  const [height, setHeight] = React.useState(
    localStorage.getItem("print-height") || "6"
  );
  const handleUnit = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUnit(event.target.value);
    localStorage.setItem("print-unit", event.target.value);
  };
  const handleWidth = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWidth(event.target.value);
    localStorage.setItem("print-width", event.target.value);
  };
  const handleHeight = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHeight(event.target.value);
    localStorage.setItem("print-height", event.target.value);
  };

  return (
    <Paper>
      <Box
        display={"flex"}
        alignContent={"space-between"}
        className="no-print"
        paddingTop="1rem"
        paddingBottom="1rem"
        gridGap="10px"
        margin={"0 1rem"}
      >
        <Button
          onClick={handlePrint}
          variant={"text"}
          color={"primary"}
          style={{
            fontWeight: "bold",
            fontSize: "1rem"
          }}
        >
          {t("common.print.print")}
        </Button>
        <TextField
          label={t("common.formFields.width")}
          variant="outlined"
          value={width}
          onChange={handleWidth}
        />
        <TextField
          label={t("common.formFields.height")}
          variant="outlined"
          value={height}
          onChange={handleHeight}
        />
        <RadioGroup
          aria-label="gender"
          name="gender1"
          value={unit}
          onChange={handleUnit}
          style={{ flexDirection: "row", padding: 10, flexShrink: 0 }}
        >
          <FormControlLabel
            value="cm"
            control={<Radio />}
            label={t("common.formFields.cm")}
          />
          <FormControlLabel
            value="in"
            control={<Radio />}
            label={t("common.formFields.inch")}
          />
        </RadioGroup>
        <div style={{ flexGrow: 1 }} />
        <Tooltip title={t("common.actions.abortPrint")}>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Tooltip>
      </Box>
      <div ref={componentRef} className={classes.printableProducts}>
        {printableProducts.map((product, index) => {
          return (
            <div
              key={product.id}
              style={{
                width: `${width}${unit}`,
                height: `${height}cm`,
                padding: "0.5rem",
                border: "1px solid lightgray"
              }}
            >
              <div className={classes.printableProduct}>
                <Typography
                  variant={"body1"}
                  className={classes.printableProductTitleFallback}
                  style={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    display: "-webkit-box",
                    maxWidth: "100%",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2
                  }}
                >
                  {product.name}
                </Typography>
                <Typography variant={"body1"}>
                  <Currency
                    value={product.pricing.price}
                    priceUnit={product.pricing.priceUnit}
                  />
                </Typography>
                <Barcode gtin={product.gtin} />
              </div>
            </div>
          );
        })}
      </div>
    </Paper>
  );
};
